import { SimplePet, PET_STAGES, getNextStage } from '@/types/simplePet';
import { PledgeBasedTarget, PledgeBasedProgress } from '@/types/pledgeBasedPlan';
import { 
  calculateDailyTargetsFromWeekly, 
  calculateTodayProgress, 
  calculateCurrentProgress 
} from '@/lib/pledgeBasedPlan';
import { Activity } from '@/types';

export interface PetPledgePerformance {
  dailyPerformance: number; // -100 to 100 (percentage of daily target)
  weeklyPerformance: number; // -100 to 100 (percentage of weekly target)
  isOnTrack: boolean;
}

export interface PetMoodState {
  emoji: string;
  message: string;
  color: string;
  healthEffect: number; // -20 to +20 daily health change
  happinessEffect: number; // -20 to +20 daily happiness change
}

export const PET_MOOD_STATES: Record<string, PetMoodState> = {
  thriving: {
    emoji: '🌟',
    message: 'Your pet is absolutely thriving! Exceeding targets gives them incredible energy!',
    color: 'text-yellow-500',
    healthEffect: 10,
    happinessEffect: 15
  },
  excellent: {
    emoji: '😊',
    message: 'Your pet is doing excellent! Meeting targets keeps them healthy and happy!',
    color: 'text-green-500',
    healthEffect: 5,
    happinessEffect: 10
  },
  good: {
    emoji: '🙂',
    message: 'Your pet is doing well! Close to targets and staying positive!',
    color: 'text-blue-500',
    healthEffect: 2,
    happinessEffect: 5
  },
  concerned: {
    emoji: '😐',
    message: 'Your pet is getting concerned about falling behind on goals...',
    color: 'text-yellow-600',
    healthEffect: -2,
    happinessEffect: -5
  },
  worried: {
    emoji: '😟',
    message: 'Your pet is worried about the lack of progress on pledge targets.',
    color: 'text-orange-500',
    healthEffect: -5,
    happinessEffect: -10
  },
  distressed: {
    emoji: '😢',
    message: 'Your pet is distressed! Far behind on pledge targets and losing energy.',
    color: 'text-red-500',
    healthEffect: -10,
    happinessEffect: -15
  },
  sick: {
    emoji: '🤒',
    message: 'Your pet is sick from the stress of missing targets! They need your help!',
    color: 'text-red-700',
    healthEffect: -15,
    happinessEffect: -20
  }
};

/**
 * Link pet to a pledge sheet for tracking
 */
export const linkPetToPledgeSheet = (pet: SimplePet, pledgeSheetId: string): SimplePet => {
  return {
    ...pet,
    pledgeSheetId,
    lastDailyUpdate: new Date(),
    lastWeeklyUpdate: new Date()
  };
};

/**
 * Calculate pet's pledge performance based on targets and progress
 */
export const calculatePetPledgePerformance = (
  dailyTargets: PledgeBasedTarget,
  todayProgress: PledgeBasedProgress,
  weeklyTargets: PledgeBasedTarget,
  weeklyProgress: PledgeBasedProgress
): PetPledgePerformance => {
  // Calculate daily performance (percentage of daily targets met)
  const dailyTotal = dailyTargets.sets + dailyTargets.openings + dailyTargets.closings + dailyTargets.closed;
  const dailyAchieved = todayProgress.sets + todayProgress.openings + todayProgress.closings + todayProgress.closed;
  const dailyPerformance = dailyTotal > 0 ? Math.round((dailyAchieved / dailyTotal) * 100) : 100;

  // Calculate weekly performance (percentage of weekly targets met)
  const weeklyTotal = weeklyTargets.sets + weeklyTargets.openings + weeklyTargets.closings + weeklyTargets.closed;
  const weeklyAchieved = weeklyProgress.sets + weeklyProgress.openings + weeklyProgress.closings + weeklyProgress.closed;
  const weeklyPerformance = weeklyTotal > 0 ? Math.round((weeklyAchieved / weeklyTotal) * 100) : 100;

  // Determine if on track (daily >= 80% or weekly >= expected for day of week)
  const dayOfWeek = new Date().getDay();
  const expectedWeeklyProgress = ((dayOfWeek + 1) / 7) * 100; // Expected progress based on day of week
  const isOnTrack = dailyPerformance >= 80 || weeklyPerformance >= expectedWeeklyProgress * 0.8;

  return {
    dailyPerformance,
    weeklyPerformance,
    isOnTrack
  };
};

/**
 * Get pet mood based on pledge performance
 */
export const getPetMoodFromPerformance = (performance: PetPledgePerformance): PetMoodState => {
  const { dailyPerformance, weeklyPerformance, isOnTrack } = performance;
  
  // Use the better of daily or weekly performance for mood calculation
  const overallPerformance = Math.max(dailyPerformance, weeklyPerformance);
  
  if (overallPerformance >= 120) return PET_MOOD_STATES.thriving;
  if (overallPerformance >= 100) return PET_MOOD_STATES.excellent;
  if (overallPerformance >= 80) return PET_MOOD_STATES.good;
  if (overallPerformance >= 60) return PET_MOOD_STATES.concerned;
  if (overallPerformance >= 40) return PET_MOOD_STATES.worried;
  if (overallPerformance >= 20) return PET_MOOD_STATES.distressed;
  return PET_MOOD_STATES.sick;
};

/**
 * Update pet health and happiness based on daily pledge performance
 */
export const updatePetWithPledgePerformance = (
  pet: SimplePet,
  weeklyTargets: PledgeBasedTarget,
  activities: Activity[]
): SimplePet => {
  if (!pet.pledgeSheetId) return pet;

  const today = new Date();
  const lastUpdate = new Date(pet.lastDailyUpdate);
  
  // Only update once per day
  if (today.toDateString() === lastUpdate.toDateString()) {
    return pet;
  }

  // Calculate daily targets and today's progress
  const dailyTargets = calculateDailyTargetsFromWeekly(weeklyTargets);
  const todayProgress = calculateTodayProgress(activities);
  
  // Calculate weekly progress for context
  const weekStartDate = new Date(today);
  weekStartDate.setDate(today.getDate() - today.getDay()); // Start of current week
  const weeklyProgress = calculateCurrentProgress(activities, weekStartDate);

  // Get performance and mood
  const performance = calculatePetPledgePerformance(dailyTargets, todayProgress, weeklyTargets, weeklyProgress);
  const mood = getPetMoodFromPerformance(performance);

  // Update pet stats based on mood
  const newHealth = Math.max(0, Math.min(100, pet.health + mood.healthEffect));
  const newHappiness = Math.max(0, Math.min(100, pet.happiness + mood.happinessEffect));

  // Update performance streak
  const newStreak = performance.isOnTrack ? pet.pledgePerformanceStreak + 1 : 0;

  // Update progress tracking
  const updatedPet: SimplePet = {
    ...pet,
    health: newHealth,
    happiness: newHappiness,
    dailyTargetProgress: todayProgress,
    weeklyPledgeProgress: weeklyProgress,
    lastDailyUpdate: today,
    pledgePerformanceStreak: newStreak
  };

  return updatedPet;
};

/**
 * Check if pet is ready for weekly settlement (evolution, recovery, etc.)
 */
export const checkWeeklyPetSettlement = (
  pet: SimplePet,
  weeklyTargets: PledgeBasedTarget
): { canEvolve: boolean; needsRecovery: boolean; weeklyBonus: number } => {
  const weeklyTotal = weeklyTargets.sets + weeklyTargets.openings + weeklyTargets.closings + weeklyTargets.closed;
  const weeklyAchieved = pet.weeklyPledgeProgress.sets + pet.weeklyPledgeProgress.openings + 
                        pet.weeklyPledgeProgress.closings + pet.weeklyPledgeProgress.closed;
  
  const weeklyPerformance = weeklyTotal > 0 ? (weeklyAchieved / weeklyTotal) * 100 : 100;
  
  // Evolution criteria: >120% weekly performance + pet health >80
  const canEvolve = weeklyPerformance >= 120 && pet.health >= 80 && getNextStage(pet.stage) !== null;
  
  // Recovery needed: <50% weekly performance or health <30
  const needsRecovery = weeklyPerformance < 50 || pet.health < 30;
  
  // Weekly bonus based on performance
  let weeklyBonus = 0;
  if (weeklyPerformance >= 120) weeklyBonus = 50;
  else if (weeklyPerformance >= 100) weeklyBonus = 30;
  else if (weeklyPerformance >= 80) weeklyBonus = 15;
  else if (weeklyPerformance >= 60) weeklyBonus = 5;
  
  return { canEvolve, needsRecovery, weeklyBonus };
};

/**
 * Process weekly settlement for the pet
 */
export const processWeeklyPetSettlement = (
  pet: SimplePet,
  weeklyTargets: PledgeBasedTarget
): SimplePet => {
  const settlement = checkWeeklyPetSettlement(pet, weeklyTargets);
  let updatedPet = { ...pet };

  // Handle evolution
  if (settlement.canEvolve) {
    const nextStage = getNextStage(pet.stage);
    if (nextStage) {
      updatedPet = {
        ...updatedPet,
        stage: nextStage,
        emoji: PET_STAGES[nextStage].emoji,
        health: 100,
        happiness: 100
      };
    }
  }

  // Handle recovery period
  if (settlement.needsRecovery) {
    updatedPet = {
      ...updatedPet,
      health: Math.max(50, updatedPet.health), // Minimum recovery to 50
      happiness: Math.max(30, updatedPet.happiness)
    };
  }

  // Reset weekly progress and update timestamp
  updatedPet = {
    ...updatedPet,
    weeklyPledgeProgress: { sets: 0, openings: 0, closings: 0, closed: 0 },
    lastWeeklyUpdate: new Date()
  };

  return updatedPet;
};

/**
 * Get daily feedback message for the pet
 */
export const getDailyPetFeedback = (
  pet: SimplePet,
  performance: PetPledgePerformance
): { title: string; message: string; mood: PetMoodState } => {
  const mood = getPetMoodFromPerformance(performance);
  
  let title = `${pet.name} ${mood.emoji}`;
  let message = mood.message;

  // Add streak information
  if (pet.pledgePerformanceStreak > 0) {
    message += ` (${pet.pledgePerformanceStreak} day streak!)`;
  }

  // Add health warning if needed
  if (pet.health < 30) {
    message += " Your pet needs immediate attention to recover!";
  }

  return { title, message, mood };
};